/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Dispatch,
  FormEvent,
  RefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';

import { saveConversationMode } from '@/sections/insights/aiAssistant/config/storage';
import {
  MAX_TEXTAREA_HEIGHT,
  SAFETY_TIMEOUT_MS,
} from '@/sections/insights/aiAssistant/constants/limits';
import type { AIAssistantMode } from '@/sections/insights/aiAssistant/types/conversation.types';
import { extractConversationId } from '@/sections/insights/aiAssistant/utils/conversationId';

interface UseMessageSendingProps {
  newMessage: string;
  setNewMessage: Dispatch<SetStateAction<string>>;
  conversationId: string | null;
  mode: AIAssistantMode;
  activeAgent: string | null;
  /**
   * Deliberate active-agent write-through (state + persist), owned by
   * AIAssistant. Sets convState.agent + mode:'agent' and persists by id.
   */
  applyActiveAgent: (agentName: string | null) => void;
  isInputDisabled: boolean;
  sendMessageWorker: (
    convId: string,
    message: string,
    mode: 'query' | 'agent'
  ) => Promise<any>;
  createConversationWorkerRaw: (
    message: string,
    mode: 'query' | 'agent'
  ) => Promise<any>;
  executeCommandIfMatch: (msg: string) => boolean;
  shouldAutoComplete: boolean;
  selectCommand: (cmd: string) => void;
  filteredCommands: Array<{ command: string; description: string; data?: any }>;
  setMessageHistory: Dispatch<SetStateAction<string[]>>;
  setHistoryIndex: Dispatch<SetStateAction<number>>;
  setSavedMessage: Dispatch<SetStateAction<string>>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}

export function useMessageSending({
  newMessage,
  setNewMessage,
  conversationId,
  mode,
  activeAgent,
  applyActiveAgent,
  isInputDisabled,
  sendMessageWorker,
  createConversationWorkerRaw,
  executeCommandIfMatch,
  shouldAutoComplete,
  selectCommand,
  filteredCommands,
  setMessageHistory,
  setHistoryIndex,
  setSavedMessage,
  textareaRef,
}: UseMessageSendingProps) {
  const [, setSearchParams] = useSearchParams();
  const [failedMessage, setFailedMessage] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  // Wrapper that saves mode when creating a conversation
  const createConversationWorker = async (
    message: string,
    convMode: 'query' | 'agent'
  ) => {
    const newConversation = await createConversationWorkerRaw(
      message,
      convMode
    );
    const newId = extractConversationId(newConversation);
    if (newId) {
      // Persist mode-only at create time; the agent name is intentionally NOT
      // persisted here because `@agent`/`/agent:` selection is conversation-gated:
      // `handleSendMessage` is only reachable via `InputForm`, which renders only
      // when a conversationId exists, so an agent can never be selected before a
      // conversation exists. If `@agent` is ever added to a no-conversation surface
      // (e.g. WelcomeView), this must also persist the agent name.
      saveConversationMode(newId, convMode);
    }
    return newConversation;
  };

  // Safety: Stop loading after 60 seconds max to prevent stuck loading states
  useEffect(() => {
    if (isWaitingForResponse) {
      const timeout = setTimeout(() => {
        setIsWaitingForResponse(false);
      }, SAFETY_TIMEOUT_MS);
      return () => clearTimeout(timeout);
    }
  }, [isWaitingForResponse]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
    }
  }, [newMessage]);

  // Auto-focus on textarea when conversation loads or changes
  useEffect(() => {
    if (textareaRef.current && conversationId && !isInputDisabled) {
      textareaRef.current.focus();
    }
  }, [conversationId, isInputDisabled]);

  const handleExampleClick = async (
    query: string,
    sendImmediately: boolean = true
  ) => {
    if (isInputDisabled) return;

    // If not sending immediately, just populate the input
    if (!sendImmediately) {
      setNewMessage(query);
      // Focus input but don't switch tabs
      setTimeout(() => textareaRef.current?.focus(), 100);
      return;
    }

    // Clear any user-typed text so the welcome textarea is empty when the
    // upcoming setSearchParams flips conversationId and mounts the main
    // composer (same H2-class race as the ChatWelcome onSendMessage path).
    setNewMessage('');

    try {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        const newConversation = await createConversationWorker(
          query,
          (mode === 'auto' ? 'agent' : mode) as 'query' | 'agent'
        );
        const newId = extractConversationId(newConversation);
        if (newId) {
          currentConversationId = newId;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
        // createConversationWorker already sent the message to the backend —
        // do NOT call sendMessageWorker here (would cause a duplicate PlannerEvent).
        // Safety-net refetches are handled inside createConversation itself.
        return;
      }

      if (!currentConversationId) {
        throw new Error('No conversation ID available');
      }

      await sendMessageWorker(
        currentConversationId,
        query,
        (mode === 'auto' ? 'agent' : mode) as 'query' | 'agent'
      );
    } catch (error) {
      console.error('Failed to send example message:', query, error);
      setFailedMessage(query);
    }
  };

  const handleEntitySearch = async (
    type: 'asset' | 'risk' | 'webpage' | 'capability',
    query: string
  ) => {
    if (!query) return;

    const searchQuery =
      type === 'asset'
        ? `Show me assets where name or dns contains "${query}"`
        : type === 'risk'
          ? `Show me risks where name or title contains "${query}"`
          : type === 'webpage'
            ? `Show me webpages where url contains "${query}"`
            : `Tell me about the ${query} capability`;

    // Send as a regular message
    if (conversationId) {
      await sendMessageWorker(
        conversationId,
        searchQuery,
        (mode === 'auto' ? 'agent' : mode) as 'query' | 'agent'
      );
    } else {
      // Create conversation with search query
      const newConversation = await createConversationWorker(
        searchQuery,
        (mode === 'auto' ? 'agent' : mode) as 'query' | 'agent'
      );
      const newId = extractConversationId(newConversation);
      if (newId) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('conversationId', newId);
          return newParams;
        });
      }
    }
  };

  const handleSendMessage = async (e: FormEvent) => {
    e?.preventDefault?.();
    if (!newMessage.trim() || isInputDisabled) return;

    const messageText = newMessage.trim();

    // Handle @agent mentions (set active agent without sending message).
    // applyActiveAgent updates the single store (agent + mode:'agent') AND
    // persists by id, so no separate saveConversationMode is needed.
    if (messageText.startsWith('@') && !messageText.includes(' ')) {
      const agentName = messageText.slice(1).trim();
      applyActiveAgent(agentName);
      setNewMessage('');
      return;
    }

    // Handle /agent:name command - set active agent but don't send as message
    if (messageText.startsWith('/agent:')) {
      const agentName = messageText.slice(7).trim();
      applyActiveAgent(agentName);
      setNewMessage('');
      return;
    }

    // Auto-complete if there's exactly one matching command.
    // selectCommand → executeCommandIfMatch may return false without
    // invoking onClearInput (e.g. for bare /mode), so defensively clear
    // here to keep this branch consistent with every other early-return.
    if (shouldAutoComplete) {
      selectCommand(filteredCommands[0].command);
      setNewMessage('');
      return;
    }

    // Check if it's a command
    if (executeCommandIfMatch(messageText)) {
      return;
    }

    // Add to message history
    setMessageHistory(prev => [...prev, messageText]);
    setHistoryIndex(-1); // Reset history index
    setSavedMessage(''); // Clear saved message on send
    setNewMessage(''); // Clear input immediately to allow rapid sending

    try {
      let currentConversationId = conversationId;

      if (!currentConversationId) {
        const newConversation = await createConversationWorker(
          messageText,
          (activeAgent || (mode === 'auto' ? 'agent' : mode)) as
            | 'query'
            | 'agent'
        );
        const newId = extractConversationId(newConversation);
        if (newId) {
          currentConversationId = newId;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('conversationId', newId);
            return newParams;
          });
        }
        // createConversationWorker already sent the message to the backend —
        // do NOT call sendMessageWorker here (would cause a duplicate PlannerEvent).
        // Safety-net refetches are handled inside createConversation itself.
        return;
      }

      if (!currentConversationId) {
        throw new Error('No conversation ID available');
      }

      await sendMessageWorker(
        currentConversationId,
        messageText,
        (activeAgent || (mode === 'auto' ? 'agent' : mode)) as 'query' | 'agent'
      );
    } catch (error) {
      console.error('Failed to send message:', messageText, error);
      setFailedMessage(messageText);
    }
  };

  return {
    failedMessage,
    setFailedMessage,
    isWaitingForResponse,
    setIsWaitingForResponse,
    handleSendMessage,
    handleExampleClick,
    handleEntitySearch,
    createConversationWorker,
  };
}
